import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { benefitService } from '../../services/benefit.service';
import { Benefit, BenefitUsage } from '../../types';
import { Loading } from '../../components/ui/Loading';

export const BenefitsPage = () => {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [usageHistory, setUsageHistory] = useState<BenefitUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBenefits();

    const interval = setInterval(() => {
      loadBenefits(true);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadBenefits = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await benefitService.getMemberBenefits();
      setBenefits(data.benefits);
      setUsageHistory(data.usageHistory);
    } catch (error: any) {
      if (!silent) toast.error(error.message || 'Failed to load benefits');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Benefits</h1>
        <p className="text-gray-600">Explore your membership benefits</p>
      </div>

      {/* Available Benefits */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Available Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">No benefits available for your membership type</p>
            </div>
          ) : (
            benefits.map((benefit) => (
              <div key={benefit.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold">{benefit.partner?.name || 'Partner Benefit'}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${benefit.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {benefit.enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4">{benefit.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Discount:</span>
                    <span className="font-medium text-green-600">{benefit.discountValue}%</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Usage History */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Usage History</h2>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {usageHistory.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No usage history yet
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Benefit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usageHistory.map((usage) => (
                  <tr key={usage.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatDate(usage.usedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {usage.benefit?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {usage.partner?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {usage.discountApplied}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
